import React, { useEffect, useState } from 'react'
import { getToken, onMessage } from 'firebase/messaging'
import GoogleMapsLoader from './components/GoogleMapsLoader/GoogleMapsLoader.js'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import AdminLayout from './layouts/Admin.jsx'
import RestaurantLayout from './layouts/Restaurant.jsx'
import AuthLayout from './layouts/Auth.jsx'
import SuperAdminLayout from './layouts/SuperAdmin.jsx'
import ServerLayout from './layouts/Server.jsx'
import AgentLayout from './layouts/Agent.jsx'
import VerifyEmail from './views/VerifyEmail.jsx'
import VerifyStaffEmail from './views/VerifyStaffEmail.jsx'
import StaffLogin from './views/StaffLogin.jsx'
import { PrivateRoute } from './views/PrivateRoute'
import { AdminPrivateRoute } from './views/AdminPrivateRoute'
import { StaffPrivateRoute } from './views/StaffPrivateRoute'
import { AgentPrivateRoute } from './views/AgentPrivateRoute'
import { HashRouter, Route, Switch, Redirect } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { isFirebaseSupported, initialize } from './firebase.js'
import { uploadToken } from './apollo'
import { gql, useApolloClient } from '@apollo/client'
import ConfigurableValues from './config/constants.js'
import useGlobalStyles from "./utils/globalStyles"
require('./i18n')

const UPLOAD_TOKEN = gql`
  ${uploadToken}
`
const App = () => {
  const {
    VAPID_KEY,
    FIREBASE_KEY,
    AUTH_DOMAIN,
    PROJECT_ID,
    STORAGE_BUCKET,
    MSG_SENDER_ID,
    APP_ID,
    MEASUREMENT_ID,
    GOOGLE_MAPS_KEY,
    SENTRY_DSN
  } = ConfigurableValues()

  const globalClasses = useGlobalStyles()
  const [isOffline, setIsOffline] = useState(!navigator.onLine)  

  useEffect(() => {
    const handleOnline = () => setIsOffline(false)  
    const handleOffline = () => setIsOffline(true)  

    window.addEventListener('online', handleOnline)  
    window.addEventListener('offline', handleOffline)  

    return () => {
      window.removeEventListener('online', handleOnline)  
      window.removeEventListener('offline', handleOffline) 
    }
  }, [])
  const handleRefresh = () => {
    window.location.reload(); 
  };
  const client = useApolloClient()
  const [user] = useState(localStorage.getItem('user-enatega'))
  const userType = localStorage.getItem('user-enatega')
    ? JSON.parse(localStorage.getItem('user-enatega')).userType
    : null
  useEffect(() => {
    if (user) {
      const parsedUser = JSON.parse(user)
      const initializeFirebase = async() => {
        if (await isFirebaseSupported()) {
          const messaging = initialize(
            FIREBASE_KEY,
            AUTH_DOMAIN,
            PROJECT_ID,
            STORAGE_BUCKET,
            MSG_SENDER_ID,
            APP_ID,
            MEASUREMENT_ID
          )
          Notification.requestPermission()
            .then(() => {
              getToken(messaging, {
                vapidKey: VAPID_KEY
              })
                .then(token => {
                  localStorage.setItem('messaging-token', token)
                  // uploadToken only works for Rider accounts; skip for admin/vendor users
                  if (parsedUser.userType === 'RIDER') {
                    client
                      .mutate({
                        mutation: UPLOAD_TOKEN,
                        variables: {
                          id: parsedUser.userId,
                          pushToken: token
                        }
                      })
                      .then(() => {
                        console.log('upload token success')
                      })
                      .catch(error => {
                        console.log('upload token error', error)
                      })
                  }
                })
                .catch(err => {
                  console.log('getToken error', err)
                })
            })
            .catch(console.log)

          onMessage(messaging, function(payload) {
            console.log(payload)
            // Customize notification here
            // const { title, body } = payload.notification
            // eslint-disable-next-line no-restricted-globals
            var notificationTitle = 'New Order on RushmyFood'
            var notificationOptions = {
              body: payload.data.orderid,
              icon: '/favicon.png'
            }
            const nt = new Notification(notificationTitle, notificationOptions)
            nt.onclick = function(event) {
              event.preventDefault() // prevent the browser from focusing the Notification's tab
              window.open('/dashboard')
              nt.close()
            }
          })
        }
      }
      initializeFirebase()
    }
  }, [user])

  
  useEffect(() => {
    if (SENTRY_DSN) {
      Sentry.init({
        dsn: SENTRY_DSN,
        environment: process.env.NODE_ENV || 'production',

        // Errors only — the Sentry plan is the free 5k events/month tier and
        // tracing would spend the budget on timing data instead of bugs.
        tracesSampleRate: 0,

        // Noise that is not our bug: extension conflicts, the harmless
        // ResizeObserver warning, and staff navigating away mid-request.
        ignoreErrors: [
          'ResizeObserver loop limit exceeded',
          'ResizeObserver loop completed with undelivered notifications',
          'Non-Error promise rejection captured',
          'Failed to fetch',
          'NetworkError when attempting to fetch resource',
          'Load failed',
          'AbortError'
        ],
        denyUrls: [
          /extensions\//i,
          /^chrome:\/\//i,
          /^chrome-extension:\/\//i,
          /^moz-extension:\/\//i,
          /^safari-extension:\/\//i
        ],

        // Same rule as the backend and web. This dashboard handles customer
        // orders, rider details and payout data — none of it belongs in a
        // third-party error report.
        beforeSend(event) {
          if (event.request) {
            delete event.request.data
            delete event.request.cookies
            if (event.request.headers) {
              delete event.request.headers.Authorization
              delete event.request.headers.Cookie
            }
          }
          return event
        }
      })
    }
  }, [SENTRY_DSN])

  // Staff session takes priority for /server routing
  const staffSession = (() => { try { return JSON.parse(localStorage.getItem('staff-session')) } catch { return null } })()

  const route = staffSession
    ? '/server/orders'
    : userType
      ? userType === 'VENDOR'
        ? '/restaurant/list'
        : userType === 'AGENT'
          ? '/agent/home'
          : '/super_admin/vendors'
      : '/auth/login'

  return (
    <Sentry.ErrorBoundary fallback={({ error, componentStack }) => (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <h2>Something went wrong</h2>
        <pre style={{ color: 'red', whiteSpace: 'pre-wrap', fontSize: 13 }}>{error?.message}</pre>
        <pre style={{ color: '#555', whiteSpace: 'pre-wrap', fontSize: 11, textAlign: 'left', background: '#f5f5f5', padding: 16, borderRadius: 8, maxHeight: 300, overflow: 'auto' }}>{componentStack}</pre>
        <button onClick={() => window.location.reload()}>Reload</button>
      </div>
    )}>
       {isOffline && (
       <Box
       component="div"
       display="flex"
       alignItems="center"
       justifyContent="center"
       flexDirection="column"
       position="fixed"
       top="0"            
       left="0"          
       width="100%"       
       height="100vh"      
       bgcolor="white"
       color="black"
       fontSize="20px"
       zIndex={1000}
     >
      <Box
      component="img"
      sx={{
        width: 100, 
        height: 'auto',
        maxWidth: { xs: '100px', sm: '200px', md: '300px' },
      }}
      alt="No internet"
      src="/nointernet.svg"
    />
      <Typography variant="body" style={{ fontSize: '16px', marginTop:"5px" }}>OOPs! No internet.</Typography>
      <Typography variant="body" style={{ fontSize: '16px', marginTop:"5px" }}>Try to refresh the page.</Typography>
       <Button 
        className={globalClasses.button}
        onClick={handleRefresh}
        style={{ marginTop: '16px' }}
      >
       Retry
      </Button>
     </Box>
      )}
      {!isOffline && (
        <>
      {true ? (
        <GoogleMapsLoader GOOGLE_MAPS_KEY={GOOGLE_MAPS_KEY}>
          <HashRouter basename="/">
            <Switch>
              <AdminPrivateRoute
                path="/super_admin"
                component={props => <SuperAdminLayout {...props} />}
              />
              <AgentPrivateRoute
                path="/agent"
                component={props => <AgentLayout {...props} />}
              />
              <PrivateRoute
                path="/restaurant"
                component={props => <RestaurantLayout {...props} />}
              />
              <PrivateRoute
                path="/admin"
                component={props => <AdminLayout {...props} />}
              />
              <StaffPrivateRoute
                path="/server"
                allowedRoles={['SERVER', 'MANAGER']}
                component={props => <ServerLayout {...props} />}
              />
              {/* render, not component: an inline arrow passed to `component`
                  is a new element type on every App re-render, so the page
                  unmounts and remounts and fires its verify mutation again. */}
              <Route
                path="/auth/verify-email"
                render={props => <VerifyEmail {...props} />}
              />
              <Route
                path="/auth/staff-verify"
                render={props => <VerifyStaffEmail {...props} />}
              />
              <Route
                path="/auth/staff-login"
                component={props => <StaffLogin {...props} />}
              />
              <Route
                path="/auth"
                component={props => <AuthLayout {...props} />}
              />
              <Redirect from="/" to={route} />
            </Switch>
          </HashRouter>
        </GoogleMapsLoader>
      ) : (
        !isOffline && (
        <Box
          component="div"
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="100vh"
          width="100vw">
          <CircularProgress color="primary" />
        </Box>
        )
      )}
       </>
      )}
    </Sentry.ErrorBoundary>
  )
}
export default Sentry.withProfiler(App)
